import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import {
  GrandfatheredUserValue,
  ServicesAgreementStatus,
  CombinedServiceAgreementStatus,
} from 'config/automatic-rollovers'
import {
  Spinner,
  FullPageSpinner,
  ComponentFailure,
  TimeoutModal,
  QuickFilterCard,
  NoTableResults,
} from 'components'
import { PlanAgreementsTable, UploadPlanAgreementModal } from '../components'
import {
  INITIAL_FILTERS,
  useServicesAgreementsFilters,
} from '../hooks/useServicesAgreementsFilters'
import {
  sanitizeForSearch,
  parseDateFromString,
  isDateInRange,
  getParticipantFileSubmitSuccessMessage,
} from 'utils'
import { selectors } from '../reducer'
import { selectors as globalSelectors } from 'global-reducer'
import { compose } from 'redux'
import { connect } from 'react-redux'
import * as apiActions from 'api-actions'
import { useLocation, useHistory } from 'react-router-dom'
import { isEqual, some } from 'lodash'
import {
  AroActionItems,
  BDActionItems,
  Path,
  AccountAudience,
  FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
  UserRole,
  isGfBDAgreementsEnabled,
} from 'config/portal'
import {
  BD_ADDENDUM_SUCCESS_MESSAGE,
  NOTIFICATION_SERVICES_ADDENDUM_SUCCESS_MESSAGE,
  NOTIFICATION_SERVICES_ORDER_SUCCESS_MESSAGE,
} from 'config/notifications'

import { ProductTypeTitles, ProductAgreementType } from 'config/products'
import { BDCombinedAgreementStatus } from 'config/benefit-distributions'
import * as actions from '../actions'
import { isBefore, isAfter, isSameDay, subDays, startOfToday } from 'date-fns'
import { Icon as CheckmarkIcon } from 'images/status-icons/checkmark.svg'
import { Icon as AlertIcon } from 'images/status-icons/alert.svg'
import { flashSuccessMessage } from 'redux-flash'
import { Icon as PlusIcon } from 'images/plus.svg'

const TABLE_ID = 'services-agreements-table'

const today = startOfToday()
const startOfRecentDate = subDays(today, 7) // recent being within the last 7 days

/* --- Quick filters criteria --- */
// Recently Approved Agreements are Services Agreements with
// a portalAgreementStatus of Completed and a createdDate within the last 7 days
const RECENTLY_APPROVED_FILTERS = {
  ...INITIAL_FILTERS,
  agreementStatus: [
    ServicesAgreementStatus.COMPLETED,
    CombinedServiceAgreementStatus.COMBINED_COMPLETED,
  ],
  startLastUpdatedDate: startOfRecentDate,
  endLastUpdatedDate: today,
}

// Missing Signatures are Services Agreements with a portalAgreementStatus of Pending signatures
const MISSING_SIGNATURES_FILTERS = {
  ...INITIAL_FILTERS,
  agreementStatus: [
    ServicesAgreementStatus.PENDING_SIGNATURES,
    CombinedServiceAgreementStatus.COMBINED_PENDING_SIGNATURES,
  ],
}

const propTypes = {
  agreementsType: PropTypes.string.isRequired,
  servicesAgreements: PropTypes.arrayOf(Types.servicesAgreementSummary),
  fetchServicesAgreements: PropTypes.func.isRequired,
  fetchBdPlanAgreements: PropTypes.func.isRequired,
  shouldTriggerAllServicesAgreementsFetch: PropTypes.bool.isRequired,
  setShouldTriggerAllServicesAgreementsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllBDPlanAgreementsFetch: PropTypes.func.isRequired,
  bdPlanAgreements: PropTypes.arrayOf(Types.bdPlanAgreementsSummary),
  shouldTriggerAllBDPlanAgreementsFetch: PropTypes.bool.isRequired,
  currentUser: Types.user.isRequired,
  flashSuccessMessageHandler: PropTypes.func.isRequired,
}

const defaultProps = {
  servicesAgreements: null,
}

function ServicesAgreementsHeader({ children, agreementsType }) {
  return (
    <div className="header-bar">
      <div className="header-bar-content">
        <div className="header-bar-page-title">
          <h1>
            {agreementsType === ProductAgreementType.ARO &&
              ProductTypeTitles.AUTOMATIC_ROLLOVERS}
            {agreementsType === ProductAgreementType.BD &&
              ProductTypeTitles.BENEFIT_DISTRIBUTIONS}
          </h1>
          {agreementsType === ProductAgreementType.BD && (
            <p>
              Ensure the plan has an automatic rollover services agreement and
              benefit distributions addendum on file before initiating a
              distribution.
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  )
}

function TableComponentFailure() {
  return (
    <div className="content-wrapper all-plans">
      <ServicesAgreementsHeader />
      <div className="services-agreements-table-wrapper">
        <ComponentFailure />
      </div>
    </div>
  )
}

function AllPlanAgreements({
  agreementsType,
  servicesAgreements,
  bdPlanAgreements,
  fetchServicesAgreements,
  fetchBdPlanAgreements,
  shouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch,
  shouldTriggerAllBDPlanAgreementsFetch,
  currentUser,
  flashSuccessMessageHandler,
}) {
  const location = useLocation()
  const history = useHistory()
  const noServicesAgreements =
    agreementsType === ProductAgreementType.ARO
      ? !servicesAgreements?.length
      : !bdPlanAgreements?.length

  useEffect(() => {
    window.appEventData.pop()
    var appEventData = window.appEventData || []
    appEventData.push({
      event: 'Page Load Completed',
    })
  }, [])
  const [error, setError] = useState(false)
  const [selectedServicesAgreement, setSelectedServicesAgreement] =
    useState(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showTimeoutModal, setShowTimeoutModal] = useState(
    !!location.state?.showTimeoutModal
  )
  const [showAroSuccessModal, setShowAroSuccessModal] = useState(
    !!location.state?.showAroSuccessModal
  )

  const [showAddendumSuccessModal, setshowAddendumSuccessModal] = useState(
    !!location.state?.showAddendumSuccessModal
  )
  const [
    showNotificationsAddendumSuccessModal,
    setShowNotificationsAddendumSuccessModal,
  ] = useState(!!location.state?.showNotificationsAddendumSuccessModal)
  const [
    showNotificationsOrderSuccessModal,
    setShowNotificationsOrderSuccessModal,
  ] = useState(!!location.state?.showNotificationsOrderSuccessModal)

  // Determines if the Automatic Rollover submission--which the user was redirected from--
  // was partial (i.e., at least one plan was missing an agreement) or complete (i.e., every plan was mapped to an agreement)
  // This information is only relevant when the component first mounts and the success modal is shown
  const isAroPartialSubmission = useMemo(
    () => !!location.state?.isPartialSubmission,
    []
  )

  useEffect(() => {
    // Reset location.state from ARO flow so the modal doesn't remain open when the page is refreshed
    history.replace(location.pathname + location.search + location.hash)

    async function getServicesAgreements() {
      try {
        await fetchServicesAgreements(agreementsType)
        setShouldTriggerAllServicesAgreementsFetch(false)
      } catch {
        setError(true)
      }
    }
    if (shouldTriggerAllServicesAgreementsFetch) getServicesAgreements()
  }, [shouldTriggerAllServicesAgreementsFetch])

  useEffect(() => {
    history.replace(location.pathname + location.search + location.hash)
    async function getBDPlanAgreements() {
      try {
        if (agreementsType === ProductAgreementType.BD) {
          await fetchBdPlanAgreements()
          setShouldTriggerAllBDPlanAgreementsFetch(false)
        }
      } catch {
        setError(true)
      }
    }

    if (
      agreementsType === ProductAgreementType.BD &&
      shouldTriggerAllBDPlanAgreementsFetch
    )
      getBDPlanAgreements()
  }, [shouldTriggerAllBDPlanAgreementsFetch])

  useEffect(() => {
    if (showAroSuccessModal) {
      const successMessage = getParticipantFileSubmitSuccessMessage(
        isAroPartialSubmission
      )
      flashSuccessMessageHandler(successMessage, {
        timeout: FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
      })
      setShowAroSuccessModal(false)
    }
  }, [
    showAroSuccessModal,
    flashSuccessMessageHandler,
    isAroPartialSubmission,
    setShowAroSuccessModal,
  ])

  useEffect(() => {
    if (showAddendumSuccessModal) {
      flashSuccessMessageHandler(BD_ADDENDUM_SUCCESS_MESSAGE, {
        timeout: FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
      })
      setshowAddendumSuccessModal(false)
    }
  }, [
    showAddendumSuccessModal,
    flashSuccessMessageHandler,
    setshowAddendumSuccessModal,
  ])
  useEffect(() => {
    if (showNotificationsAddendumSuccessModal) {
      flashSuccessMessageHandler(
        NOTIFICATION_SERVICES_ADDENDUM_SUCCESS_MESSAGE,
        {
          timeout: FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
        }
      )
      setShowNotificationsAddendumSuccessModal(false)
    }
  }, [
    showNotificationsAddendumSuccessModal,
    flashSuccessMessageHandler,
    setShowNotificationsAddendumSuccessModal,
  ])
  useEffect(() => {
    if (showNotificationsOrderSuccessModal) {
      flashSuccessMessageHandler(NOTIFICATION_SERVICES_ORDER_SUCCESS_MESSAGE, {
        timeout: FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
      })
      setShowNotificationsOrderSuccessModal(false)
    }
  }, [
    showNotificationsOrderSuccessModal,
    flashSuccessMessageHandler,
    setShowNotificationsOrderSuccessModal,
  ])

  const {
    filters,
    searchValue,
    updateSearchParams,
    filteredServicesAgreements,
  } = useServicesAgreementsFilters({ servicesAgreements, currentUser })

  const filteredBDPlanAgreements = useMemo(() => {
    if (!bdPlanAgreements) return

    const filteredBDPlanAgreements = bdPlanAgreements.filter(
      ({
        planSponsorName,
        addendumStatus,
        agreementStatus: bdAgreementStatus,
        dateCreated,
      }) => {
        const {
          agreementStatus,
          startLastUpdatedDate,
          endLastUpdatedDate,
          planSponsor,
        } = filters
        const createdDate = parseDateFromString(dateCreated)
        return (
          (agreementStatus.length
            ? agreementStatus.includes(addendumStatus) ||
              agreementStatus.includes(bdAgreementStatus)
            : true) &&
          (planSponsor.length ? planSponsor.includes(planSponsorName) : true) &&
          (startLastUpdatedDate
            ? isAfter(createdDate, startLastUpdatedDate) ||
              isSameDay(createdDate, startLastUpdatedDate)
            : true) &&
          (endLastUpdatedDate
            ? isBefore(createdDate, endLastUpdatedDate) ||
              isSameDay(createdDate, endLastUpdatedDate)
            : true)
        )
      }
    )

    // ignore hyphens and case
    const sanitizedValue = sanitizeForSearch(searchValue)
    if (sanitizedValue === '') return filteredBDPlanAgreements
    if (currentUser.accountAudience === AccountAudience.PLAN_FIDUCIARY) {
      return filteredBDPlanAgreements.filter(({ completeLegalPlanName }) =>
        sanitizeForSearch(completeLegalPlanName).includes(sanitizedValue)
      )
    }

    return filteredBDPlanAgreements.filter(
      ({ planSponsorName, ein, completeLegalPlanName }) =>
        sanitizeForSearch(completeLegalPlanName).includes(sanitizedValue) ||
        sanitizeForSearch(planSponsorName).includes(sanitizedValue) ||
        (ein && sanitizeForSearch(ein).includes(sanitizedValue))
    )
  }, [filters, searchValue, bdPlanAgreements])

  const isLoading =
    agreementsType === ProductAgreementType.ARO
      ? !servicesAgreements && !error
      : !bdPlanAgreements && !error

  if (isLoading) return <FullPageSpinner />
  if (error) return <TableComponentFailure />

  const hasCompletedServicesAgreement = some(
    servicesAgreements,
    (agreement) =>
      agreement.portalAgreementStatus === ServicesAgreementStatus.COMPLETED ||
      agreement.portalAgreementStatus ===
        CombinedServiceAgreementStatus.COMBINED_COMPLETED
  )

  const hasCompletedAddendum = some(
    bdPlanAgreements,
    (agreement) =>
      agreement.addendumStatus === ServicesAgreementStatus.COMPLETED ||
      agreement.addendumStatus === BDCombinedAgreementStatus.COMBINED_COMPLETED
  )

  const handleAddPlanAgreementAction = () =>
    history.push({
      pathname:
        agreementsType === ProductAgreementType.BD
          ? Path.ADD_NEW_BD_SERVICES_AGREEMENT
          : Path.ADD_NEW_SERVICES_AGREEMENT,
      state: {
        search: history.location.search,
      },
    })

  const handleAddNewAddendumAction = () =>
    history.push({
      pathname: Path.ADD_NEW_BD_ADDENDUM,
      state: {
        ignorePrompt: true,
      },
    })

  /**
   * @description Handles onClick action for when the user clicks on ***Initiate Rollover*** or ***Initiate Distribution*** button
   */
  const handleInitiateParticipantUploadAction = () =>
    history.push({
      pathname:
        agreementsType === ProductAgreementType.ARO
          ? '/automatic-rollovers/select-rollover-type'
          : agreementsType === ProductAgreementType.BD
          ? '/benefit-distributions/select-payment-method'
          : '/notification-services/upload-file',
      state: {
        hasConfirmed: true,
        search: location.search,
      },
    })

  // Quick filters counter
  const missingSignaturesCount =
    agreementsType === ProductAgreementType.ARO
      ? servicesAgreements.reduce((acc, { portalAgreementStatus }) => {
          if (
            portalAgreementStatus ===
              ServicesAgreementStatus.PENDING_SIGNATURES ||
            portalAgreementStatus ===
              CombinedServiceAgreementStatus.COMBINED_PENDING_SIGNATURES
          )
            acc++
          return acc
        }, 0)
      : 0

  const recentlyApprovedAgreementsCount =
    agreementsType === ProductAgreementType.ARO
      ? servicesAgreements.reduce(
          (acc, { portalAgreementStatus, createdDate }) => {
            const isRecent = isDateInRange({
              date: createdDate,
              startOfDateRange: startOfRecentDate,
              endOfDateRange: today,
            })
            if (
              (portalAgreementStatus === ServicesAgreementStatus.COMPLETED ||
                portalAgreementStatus ===
                  CombinedServiceAgreementStatus.COMBINED_COMPLETED) &&
              isRecent
            )
              acc++
            return acc
          },
          0
        )
      : 0

  const fieldMappings = [
    { field: 'planSponsorCompany.name', displayName: 'Plan Sponsor Name' },
    { field: 'planEIN', displayName: 'EIN' },
    { field: 'name', displayName: 'Complete Legal Plan Name' },
    { field: 'portalAgreementType', displayName: 'Initial Investment' },
    { field: 'portalAgreementStatus', displayName: 'Agreement Status' },
    { field: 'createdDate', displayName: 'Date Created' },
  ]

  const bdplanAgreementsFieldMappings = [
    { field: 'planSponsorName', displayName: 'Plan Sponsor Name' },
    { field: 'ein', displayName: 'EIN' },
    { field: 'completeLegalPlanName', displayName: 'Complete Legal Plan Name' },
    { field: 'accountNumber', displayName: 'Account Number' },
    {
      field: 'exportStatus',
      displayName: !isGfBDAgreementsEnabled()
        ? 'Addendum Status'
        : 'Agreement Status',
    },
    { field: 'dateCreated', displayName: 'Date Created' },
  ]

  const planFiduciaryFieldMappingsForARO = fieldMappings.slice(2)

  const planFiduciaryFieldMappingsForBD = bdplanAgreementsFieldMappings.slice(2)

  const agreementFieldMappings =
    currentUser.accountAudience === AccountAudience.PLAN_FIDUCIARY
      ? agreementsType === ProductAgreementType.ARO
        ? planFiduciaryFieldMappingsForARO
        : planFiduciaryFieldMappingsForBD
      : agreementsType === ProductAgreementType.ARO
      ? fieldMappings
      : bdplanAgreementsFieldMappings

  const benefiDistributionsAgreementsActons = () => {
    return (
      <>
        <button
          type="button"
          onClick={() => handleAddPlanAgreementAction()}
          className="button-text"
        >
          <PlusIcon aria-hidden="true" />
          {BDActionItems.CREATE_NEW_SERVICES_AGREEMENT}
        </button>
        {hasCompletedServicesAgreement && (
          <button
            type="button"
            onClick={() => handleInitiateParticipantUploadAction()}
            className="button-primary"
          >
            {BDActionItems.INITIATE_DISTRIBUTION}
          </button>
        )}
      </>
    )
  }

  const benefiDistributionsAddendumActions = () => {
    return (
      <>
        <button
          type="button"
          onClick={handleAddNewAddendumAction}
          className="button-text"
        >
          <PlusIcon aria-hidden="true" />
          {BDActionItems.CREATE_NEW_ADDENDUM}
        </button>
        {hasCompletedAddendum && (
          <button
            type="button"
            onClick={() => handleInitiateParticipantUploadAction()}
            className="button-primary"
          >
            {BDActionItems.INITIATE_DISTRIBUTION}
          </button>
        )}
      </>
    )
  }

  const aroActionItems = () => {
    return (
      <>
        <button
          type="button"
          onClick={() => handleAddPlanAgreementAction()}
          className="button-text"
        >
          <PlusIcon aria-hidden="true" />
          {AroActionItems.CREATE_NEW_SERVICES_AGREEMENT}
        </button>
        {hasCompletedServicesAgreement && (
          <button
            type="button"
            onClick={() => handleInitiateParticipantUploadAction()}
            className="button-primary"
          >
            {AroActionItems.INITIATE_ROLLOVER}
          </button>
        )}
      </>
    )
  }

  const productActionItems = () => {
    if (agreementsType === ProductAgreementType.ARO) {
      return aroActionItems()
    } else if (agreementsType === ProductAgreementType.BD) {
      return (
        <>
          {currentUser?.grandfatheredUser === GrandfatheredUserValue &&
          isGfBDAgreementsEnabled()
            ? benefiDistributionsAgreementsActons()
            : benefiDistributionsAddendumActions()}
        </>
      )
    }
  }

  return (
    <div className="content-wrapper all-plans">
      <ServicesAgreementsHeader agreementsType={agreementsType}>
        <div className="services-agreements-actions">
          {productActionItems()}
        </div>
      </ServicesAgreementsHeader>
      {agreementsType === ProductAgreementType.ARO && (
        <div className="quick-filters-container">
          <div className="flex-columns">
            <div className="flex-column">
              <QuickFilterCard
                controlId={TABLE_ID}
                isActive={isEqual(filters, RECENTLY_APPROVED_FILTERS)}
                count={recentlyApprovedAgreementsCount}
                label="Recently approved agreements"
                icon={CheckmarkIcon}
                onClick={() => {
                  if (!isEqual(filters, RECENTLY_APPROVED_FILTERS))
                    updateSearchParams(RECENTLY_APPROVED_FILTERS, searchValue)
                  else updateSearchParams(INITIAL_FILTERS, searchValue)
                }}
              />
            </div>
            {(currentUser.accountAudience !== AccountAudience.PLAN_FIDUCIARY ||
              (currentUser.accountAudience === AccountAudience.PLAN_FIDUCIARY &&
                currentUser.retirementServicesPortalProfile ===
                  UserRole.ADMIN)) && (
              <div className="flex-column">
                <QuickFilterCard
                  controlId={TABLE_ID}
                  isActive={isEqual(filters, MISSING_SIGNATURES_FILTERS)}
                  count={missingSignaturesCount}
                  label="Missing signatures"
                  icon={AlertIcon}
                  onClick={() => {
                    if (!isEqual(filters, MISSING_SIGNATURES_FILTERS))
                      updateSearchParams(
                        MISSING_SIGNATURES_FILTERS,
                        searchValue
                      )
                    else updateSearchParams(INITIAL_FILTERS, searchValue)
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
      {agreementsType === ProductAgreementType.ARO &&
        shouldTriggerAllServicesAgreementsFetch && <Spinner />}
      {agreementsType === ProductAgreementType.BD &&
        shouldTriggerAllBDPlanAgreementsFetch && <Spinner />}
      <PlanAgreementsTable
        agreementsType={agreementsType}
        currentUser={currentUser}
        filters={filters}
        tableId="services-agreements-table"
        servicesAgreements={filteredServicesAgreements}
        bdPlanAgreements={filteredBDPlanAgreements}
        searchValue={searchValue}
        noServicesAgreements={noServicesAgreements}
        fieldMappings={agreementFieldMappings}
        noRowsFoundMessage={
          agreementsType !== ProductAgreementType.BD ? (
            <NoTableResults
              title={
                noServicesAgreements
                  ? 'You don’t have any service agreements at this time.'
                  : 'No results found.'
              }
              titleAs="h2"
              details={
                noServicesAgreements
                  ? 'Need to create a new agreement? You can do that now.'
                  : 'Try adjusting your filter or search.'
              }
            >
              {noServicesAgreements && (
                <button
                  type="button"
                  className="button-primary"
                  onClick={handleAddPlanAgreementAction}
                >
                  {AroActionItems.CREATE_NEW_SERVICES_AGREEMENT}
                </button>
              )}
            </NoTableResults>
          ) : (
            <NoTableResults
              title={
                !hasCompletedAddendum
                  ? 'You don’t have any addendums at this time.'
                  : 'No results found.'
              }
              titleAs="h2"
              details={
                !hasCompletedAddendum
                  ? 'Need to create a new addendum? You can do that now.'
                  : 'Try adjusting your filter or search.'
              }
            >
              {!hasCompletedAddendum && (
                <button
                  type="button"
                  className="button-primary"
                  onClick={handleAddNewAddendumAction}
                >
                  {BDActionItems.CREATE_NEW_ADDENDUM}
                </button>
              )}
            </NoTableResults>
          )
        }
        handleUploadClick={(servicesAgreement) => {
          setSelectedServicesAgreement(servicesAgreement)
          setShowUploadModal(true)
        }}
      />
      {showUploadModal && (
        <UploadPlanAgreementModal
          onClose={() => {
            setShowUploadModal(false)
            setSelectedServicesAgreement(null)
          }}
          servicesAgreement={selectedServicesAgreement}
          onSubmitSuccess={() => {
            setShowUploadModal(false)
            setSelectedServicesAgreement(null)
            setShouldTriggerAllServicesAgreementsFetch(true)
            setShouldTriggerAllBDPlanAgreementsFetch(true)
          }}
          agreementsType={agreementsType}
        />
      )}
      {showTimeoutModal && (
        <TimeoutModal onClose={() => setShowTimeoutModal(false)} />
      )}
    </div>
  )
}

function mapStateToProps(state) {
  return {
    servicesAgreements: selectors.servicesAgreements(state),
    shouldTriggerAllServicesAgreementsFetch:
      selectors.shouldTriggerAllServicesAgreementsFetch(state),
    currentUser: globalSelectors.currentUser(state),
    bdPlanAgreements: selectors.bdPlanAgreements(state),
    shouldTriggerAllBDPlanAgreementsFetch:
      selectors.shouldTriggerAllBDPlanAgreementsFetch(state),
  }
}

const mapDispatchToProps = {
  fetchServicesAgreements: apiActions.fetchServicesAgreements,
  fetchBdPlanAgreements: apiActions.fetchBdPlanAgreements,
  setShouldTriggerAllServicesAgreementsFetch:
    actions.setShouldTriggerAllServicesAgreementsFetch,
  flashSuccessMessageHandler: flashSuccessMessage,
  setShouldTriggerAllBDPlanAgreementsFetch:
    actions.setShouldTriggerAllBDPlanAgreementsFetch,
}

AllPlanAgreements.propTypes = propTypes
AllPlanAgreements.defaultProps = defaultProps

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  AllPlanAgreements
)
